import React, { useState, useEffect } from 'react';
import { Send, User, Mail, MessageSquare, Tag, AlertCircle, Loader2 } from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';

interface UniversalRequestFormProps {
  organizationId?: string;
  companyName?: string;
}

export const UniversalRequestForm: React.FC<UniversalRequestFormProps> = ({
  organizationId,
  companyName
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    category: 'consultation',
    title: '',
    description: '',
    urgency: 'medium'
  });

  // Parse URL parameters for pre-filling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    setFormData(prev => ({
      ...prev,
      customerName: urlParams.get('prefill_name') || prev.customerName,
      category: urlParams.get('prefill_category') || prev.category,
      title: urlParams.get('prefill_title') || prev.title,
      description: urlParams.get('prefill_description') || prev.description
    }));
  }, []);

  // Get org ID and company name from URL/context
  const currentPath = window.location.pathname;
  const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
  const finalOrgId = organizationId || (orgMatch && orgMatch[1].startsWith('org_') ? orgMatch[1] : '');
  const finalCompanyName = companyName || 'Universal Support';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.customerName.trim()) {
        throw new Error('Please enter your name');
      }
      if (!formData.customerEmail.trim()) {
        throw new Error('Please enter your email');
      }
      if (!formData.description.trim()) {
        throw new Error('Please describe your request');
      }

      // Submit to the public universal request endpoint with org ID in path (secure)
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/universal/submit-request/${finalOrgId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Note: No organization_id in body - extracted securely from URL path on backend
          participant_data: {
            customer_name: formData.customerName.trim(),
            customer_email: formData.customerEmail.trim(),
            name: formData.customerName.trim(),
            email: formData.customerEmail.trim()
          },
          project_details: {
            title: formData.title.trim() || 'Universal Consultation Request',
            category: formData.category,
            urgency: formData.urgency,
            description: formData.description.trim()
          },
          organization_config: {
            company_name: finalCompanyName,
            host_name: 'Universal Agent'
          },
          metadata: {
            source: 'public_form',
            submitted_at: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('[UniversalRequestForm] Request submitted:', result);

      // Show success and redirect to waiting room
      setSuccess(true);
      
      // Redirect to waiting room after brief success message
      setTimeout(() => {
        // Always use org-scoped waiting room URL instead of external URL
        const orgPrefix = finalOrgId ? `/${finalOrgId}` : '';
        const waitingRoomUrl = `${orgPrefix}/templates/universal/waiting/${result.session_id}?sessionId=${result.session_id}&external=true`;
        console.log('[UniversalRequestForm] Redirecting to waiting room:', waitingRoomUrl);
        window.location.href = waitingRoomUrl;
      }, 2000);

    } catch (error) {
      console.error('[UniversalRequestForm] Submission error:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (success) {
    return (
      <BrandingProvider>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Thank you {formData.customerName}! Your consultation request has been received.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you to the waiting room...
            </p>
          </div>
        </div>
      </BrandingProvider>
    );
  }

  return (
    <BrandingProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Request Universal Consultation
            </h1>
            <p className="text-gray-600">
              Submit your request to connect with {finalCompanyName}
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Your Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="customerEmail"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Request Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="consultation">General Consultation</option>
                      <option value="support">Technical Support</option>
                      <option value="sales">Sales Inquiry</option>
                      <option value="billing">Billing Question</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Priority Level
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="low">Low - General inquiry</option>
                      <option value="medium">Medium - Standard request</option>
                      <option value="high">High - Important matter</option>
                      <option value="urgent">Urgent - Needs immediate attention</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief subject line"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Please describe your request or question in detail..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description.length}/500 characters
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-75 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-500">
            <p>
              By submitting this form, you'll be connected with a {finalCompanyName} representative
            </p>
            <p className="mt-1">
              Expected response time: 15-30 minutes during business hours
            </p>
          </div>
        </div>
      </div>
    </BrandingProvider>
  );
};